import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { CardModule } from 'primeng/card';
import { Table, TableModule } from 'primeng/table';
import { CheckboxModule } from 'primeng/checkbox';
import { TranslateModule } from '@ngx-translate/core';
import { TooltipModule } from 'primeng/tooltip';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { FilterService } from 'primeng/api';
import { BadgeModule } from 'primeng/badge';
import { SelectModule, SelectChangeEvent } from 'primeng/select';
import { ValidatorComponent } from 'src/app/ui/validator/validator.component';
import { KeycloakInfoService } from 'src/app/services/keycloak-info.service';

import { GroupsService } from '../../../services/groups.service';
import { UsersService } from '../../../services/users.service';
import { CreateGroupDTO, UpdateGroupDTO, UserDTO, GroupDTO } from '../../../model/admin-dto';
import { LayoutService } from '../../../layout/service/layout.service';
import { PageTitleComponent } from '../../../ui/page-title/page-title.component';
import { Roles } from '../../../model/roles.enum';

@Component({
  selector: 'rqa-add-group',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    ButtonModule,
    InputTextModule,
    CardModule,
    TableModule,
    CheckboxModule,
    TranslateModule,
    TooltipModule,
    IconFieldModule,
    InputIconModule,
    BadgeModule,
    SelectModule,
    ValidatorComponent,
    PageTitleComponent
  ],
  templateUrl: './add-group.component.html',
  styleUrl: './add-group.component.scss',
  providers: [GroupsService, UsersService, FilterService]
})
export class AddGroupComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly layoutService = inject(LayoutService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly groupsService = inject(GroupsService);
  private readonly usersService = inject(UsersService);
  private readonly keycloakInfoService = inject(KeycloakInfoService);

  groupForm: FormGroup;
  isSubmitting = false;
  isEditMode = false;
  groupId: string | null = null;
  groups = this.keycloakInfoService.groups;
  selectedResponsibleUnit: string | null = null;
  filtersForm: FormGroup = this.fb.group({
    responsibleUnit: [''],
    organization: ['']
  });

  readonly users = signal<UserDTO[]>([]);
  readonly filteredUsers = signal<UserDTO[]>([]);
  readonly usersLoading = signal<boolean>(false);
  readonly groupLoading = signal<boolean>(false);
  readonly tableScrollHeight = computed(() => this.layoutService.calculateTableHeightAddGroup());
  selectedUsers: UserDTO[] = [];

  get nameControl() {
    return this.groupForm.get('name');
  }

  constructor() {
    this.groupForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      group: ['', [Validators.required]]
    });
  }

  ngOnInit(): void {
    this.checkEditMode();
    this.loadUsers();
    this.onFiltersFormChange();
  }

  loadUsers(): void {
    this.usersLoading.set(true);

    this.usersService.getUsersList().subscribe({
      next: (users) => {
        this.users.set(users.filter((user) => user.enabled === true && user.role !== Roles.WEBRQA_OBSERVER));
        if (this.isEditMode) {
          this.loadGroupData();
        }
        this.usersLoading.set(false);
      },

      error: (error) => {
        console.error('Error loading users:', error);
        this.usersLoading.set(false);
      }
    });
  }

  onSubmit(): void {
    if (this.groupForm.valid && !this.isSubmitting) {
      this.isSubmitting = true;

      if (this.isEditMode && this.groupId) {
        const updateGroupData: UpdateGroupDTO = {
          id: this.groupId,
          name: this.groupForm.value.name.trim(),
          group: this.groupForm.value.group,
          users: this.selectedUsers.map((user) => user.username)
        };

        this.groupsService.updateGroup(updateGroupData).subscribe({
          next: (response) => {
            console.log('Group updated successfully:', response);
            this.router.navigate(['/groups']);
          },
          error: (error) => {
            console.error('Error updating group:', error);
            this.isSubmitting = false;
          }
        });
      } else {
        const createGroupData: CreateGroupDTO = {
          name: this.groupForm.value.name.trim(),
          group: this.groupForm.value.group,
          users: this.selectedUsers.map((user) => user.username)
        };

        this.groupsService.addGroup(createGroupData).subscribe({
          next: (response) => {
            console.log('Group created successfully:', response);
            this.router.navigate(['/groups']);
          },
          error: (error) => {
            console.error('Error creating group:', error);
            this.isSubmitting = false;
          }
        });
      }
    }
  }

  onCancel(): void {
    this.router.navigate(['/groups']);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSelectionChange(event: any): void {
    if (event.length === 0) {
      this.selectedResponsibleUnit = null;
      this.filteredUsers.set(this.users());
    } else {
      this.selectedResponsibleUnit = event[0].responsibleUnit;
      this.filteredUsers.set(this.users().filter((user) => user.responsibleUnit === event[0].responsibleUnit));
    }
  }

  onGroupChange(event: SelectChangeEvent): void {
    this.filteredUsers.set(this.users().filter((user) => user.group === event.value));
    this.selectedUsers = [];
    this.selectedResponsibleUnit = null;
  }

  onFiltersFormChange(): void {
    this.filtersForm.valueChanges.subscribe((value) => {
      this.filteredUsers.set(
        this.users().filter(
          (user) =>
            user.responsibleUnit.toLowerCase().includes(value.responsibleUnit.toLowerCase()) &&
            user.organization.toLowerCase().includes(value.organization.toLowerCase()) &&
            user.group === this.groupForm.value.group
        )
      );
    });
  }

  filterGroupUsers(event: Event, groupUsersTable: Table): void {
    groupUsersTable.filterGlobal((event.target as HTMLInputElement).value, 'contains');
  }

  private checkEditMode(): void {
    this.groupId = this.route.snapshot.paramMap.get('id');
    this.isEditMode = !!this.groupId;
  }

  private loadGroupData(): void {
    if (!this.groupId) return;

    this.groupLoading.set(true);
    this.groupsService.getGroupById(this.groupId).subscribe({
      next: (group) => {
        this.populateFormWithGroupData(group);
        this.selectedResponsibleUnit = group.webRqaUsers[0].responsibleUnit;
        this.groupLoading.set(false);
        this.filteredUsers.set(
          this.users().filter(
            (user) => user.group === group.groupValue && user.responsibleUnit === group.webRqaUsers[0].responsibleUnit
          )
        );
      },
      error: (error) => {
        console.error('Error loading group:', error);
        this.groupLoading.set(false);
      }
    });
  }

  private populateFormWithGroupData(group: GroupDTO): void {
    this.groupForm.patchValue({
      name: group.name,
      group: group.groupValue
    });

    if (group.webRqaUsers && group.webRqaUsers.length > 0) {
      this.setSelectedUsersFromGroup(group.webRqaUsers);
    }
  }

  private setSelectedUsersFromGroup(groupUsers: UserDTO[]): void {
    const groupUsernames = groupUsers.map((user) => user.username);
    const selectedUsers = this.users().filter((user) => groupUsernames.includes(user.username));

    this.selectedUsers = selectedUsers;
  }
}
